import orElse from 'volta/helpers/or-else';
import BvCalendar from 'volta/components/bv-calendar';
import BvDateField from 'volta/components/bv-date-field';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvLabelled from 'volta/components/bv-labelled';
import BvPopover from 'volta/components/bv-popover';
import BvTag from 'volta/components/bv-tag';
import { fn } from '@ember/helper';
import { action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import { set } from 'ember-set-helper';
import eq from 'ember-truth-helpers/helpers/eq';
import _isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { utcDateOrUndefined } from 'volta/utils/date-utils';

import type { BvDateFieldSignature } from 'volta/components/bv-date-field';
import type { BvPopoverSignature } from 'volta/components/bv-popover';
import type { BvLabelledSignature } from './../bv-labelled';
import type { NormalizeRangeActionValue } from 'ember-power-calendar/utils';
import type { IDateFilter } from 'volta/_pods/protected/bom/controller';
import type { TDate } from 'volta/models/types/calendar';

interface BvDateRangeFieldSignature {
  Args: Pick<BvLabelledSignature['Args'], 'label' | 'labelHidden'> &
    Pick<BvDateFieldSignature['Args'], 'allowClear'> & {
      dateFilter?: IDateFilter;
      onChange?: (filter: IDateFilter) => void;
      inlineCalendar?: boolean;
      minStartDate?: TDate;
      startDatePlaceholder?: string;
      endDatePlaceholder?: string;
      calendarSize?: 'sm' | 'md' | 'lg';
      horizontalPosition?: BvPopoverSignature['Args']['horizontalPosition'];
    };
  Element: HTMLDivElement;
}

export default class BvDateRangeFieldComponent extends Component<BvDateRangeFieldSignature> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    {{#let
      (component
        BvDateField
        value=this.selectedStartDate
        onChange=this.handleStartDateChange
        minDate=@minStartDate
        maxDate=this.selectedEndDate
        allowClear=(orElse @allowClear true)
        withoutCalendar=true
        size='sm'
        labelHidden=true
        placeholder=@startDatePlaceholder
        suffix=(component BvIcon glyph='calendar-outline')
      )
      (component
        BvDateField
        value=this.selectedEndDate
        onChange=this.handleEndDateChange
        minDate=this.selectedStartDate
        allowClear=(orElse @allowClear true)
        withoutCalendar=true
        size='sm'
        labelHidden=true
        placeholder=@endDatePlaceholder
        suffix=(component BvIcon glyph='calendar-outline')
      )
      (component
        BvCalendar
        center=this.calCenter
        onCenterChange=this.handleCenterChange
        selected=this.selectedRange
        onSelect=this.onCalendarSelect
        withRange=true
        minRange=0
        size=(orElse @calendarSize 'sm')
        minDate=@minStartDate
        fullWidth=true
      )
      as |StartDate EndDate Calendar|
    }}
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <BvLabelled
        @label={{@label}}
        @labelHidden={{@labelHidden}}
        {{didUpdate this.handleValueChange @dateFilter}}
        ...attributes
      >
        {{#if @inlineCalendar}}
          <BvInline @gap='1' @blockAlign='center' @align='space-between' @wrap={{false}}>
            <StartDate
              @onFocus={{set this 'focusedOn' 'start'}}
              @focused={{eq this.focusedOn 'start'}}
            />
            <BvTag @text='~' @size='sm' class='u-px-1 u-flex-shrink-0' />
            <EndDate @onFocus={{set this 'focusedOn' 'end'}} @focused={{eq this.focusedOn 'end'}} />
          </BvInline>
          <Calendar class='u-mt-2 u-mx-auto' />
        {{else}}
          <BvPopover
            @size={{orElse @calendarSize 'lg'}}
            @horizontalPosition={{orElse @horizontalPosition 'center'}}
            @onOpen={{this.handlePopoverOpen}}
            as |popover|
          >
            <BvInline
              @gap='2'
              @blockAlign='center'
              @align='space-between'
              @wrap={{false}}
              {{popover.trigger}}
            >
              <StartDate
                @onFocus={{fn (queue popover.open (set this 'focusedOn' 'start'))}}
                @focused={{eq this.focusedOn 'start'}}
              />
              <BvTag @text='~' @size='sm' class='u-px-1 u-flex-shrink-0' />
              <EndDate
                @onFocus={{fn (queue popover.open (set this 'focusedOn' 'end'))}}
                @focused={{eq this.focusedOn 'end'}}
              />
            </BvInline>
            <popover.Content>
              <popover.Body>
                <Calendar @showNextMonth={{true}} @afterSelect={{popover.close}} />
              </popover.Body>
            </popover.Content>
          </BvPopover>
        {{/if}}
      </BvLabelled>
    {{/let}}
  </template>
  @tracked selectedStartDate?: TDate = this.args.dateFilter?.startDate;
  @tracked selectedEndDate?: TDate = this.args.dateFilter?.endDate;
  @tracked calCenter: Date = moment.utc(this.selectedStartDate ?? this.selectedEndDate).toDate();
  @tracked focusedOn?: 'start' | 'end';

  get selection() {
    return {
      startDate: utcDateOrUndefined(this.selectedStartDate),
      endDate: utcDateOrUndefined(this.selectedEndDate)
    };
  }

  get selectedRange() {
    const { startDate, endDate } = this.selection;
    return {
      start: startDate,
      end: endDate
    };
  }

  @action
  handleCenterChange(center: { date: Date }) {
    if (center.date) {
      this.calCenter = moment.utc(center.date).toDate();
    }
  }

  @action
  handlePopoverOpen() {
    this.calCenter = moment.utc(this.selectedStartDate ?? this.selectedEndDate).toDate();
  }

  @action
  handleStartDateChange(date?: TDate) {
    this.selectedStartDate = utcDateOrUndefined(date);
    if (date) {
      this.calCenter = moment.utc(date).toDate();
    }

    this.args.onChange?.(this.selection);
  }

  @action
  onCalendarSelect(days: NormalizeRangeActionValue) {
    const { start, end } = days.date;
    // When the end date has the focus and no start date is defined
    // It seems better to give the value to the end date
    // It might not be the case in all situation
    const newEndDate = end ?? start;
    if (
      this.focusedOn === 'end' &&
      (!this.selectedStartDate || moment.utc(newEndDate).isAfter(this.selectedStartDate))
    ) {
      this.selectedEndDate = utcDateOrUndefined(newEndDate);
    } else {
      this.selectedStartDate = utcDateOrUndefined(start);
      this.selectedEndDate = utcDateOrUndefined(end);

      if (start && !end) {
        this.focusedOn = 'end';
      }
    }
    this.args.onChange?.(this.selection);
  }

  @action
  handleEndDateChange(date?: TDate) {
    this.selectedEndDate = utcDateOrUndefined(date);
    if (date) {
      this.calCenter = moment.utc(date).toDate();
    }
    this.args.onChange?.(this.selection);
  }

  /**
   * This handler reset the start and end date values when the dateFilter is updated externally
   */
  @action
  handleValueChange(_el: HTMLElement, [value]: [IDateFilter | undefined]) {
    const { selection } = this;
    if (value && !_isEqual(selection, value)) {
      this.selectedStartDate = utcDateOrUndefined(value.startDate);
      this.selectedEndDate = utcDateOrUndefined(value.endDate);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvDateRangeField: typeof BvDateRangeFieldComponent;
    'bv-date-range-field': typeof BvDateRangeFieldComponent;
  }
}
